import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ShieldCheck, Users } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-copper">Private Messages</p>
          <h1 className="font-serif text-3xl font-bold text-bronze">Your conversations</h1>
        </div>
        <Button data-testid="go-community-btn" onClick={() => navigate("/community")} variant="outline" className="rounded-full">
          <Users className="w-4 h-4 mr-1.5" /> Community Room
        </Button>
      </div>
      <div className="flex items-center gap-2 text-xs text-sage bg-sage/10 rounded-full px-3 py-1.5 w-fit mb-5">
        <ShieldCheck className="w-3.5 h-3.5" /> Be kind. Harassment means an instant, permanent ban.
      </div>
      <DirectMessages initialUser={userId} />
    </div>
  );
}

function DirectMessages({ initialUser }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(initialUser || null);
  const [thread, setThread] = useState({ messages: [], partner: null });
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadConvs = useCallback(() => api.get("/dm/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);
  useEffect(() => { loadConvs(); }, [loadConvs]);

  const loadThread = useCallback(() => {
    if (!active) return;
    api.get(`/dm/${active}`).then((r) => setThread(r.data)).catch(() => {});
  }, [active]);
  useEffect(() => { loadThread(); const i = setInterval(loadThread, 4000); return () => clearInterval(i); }, [loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try { await api.post("/dm", { to_user_id: active, text }); setText(""); loadThread(); loadConvs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="bg-card rounded-3xl border border-border grid md:grid-cols-3 h-[60vh] overflow-hidden">
      <div className="border-r border-border overflow-y-auto no-scrollbar">
        {convs.length === 0 ? (
          <p className="text-xs text-muted-foreground p-4">No conversations yet. Visit a profile and tap the message icon to start one.</p>
        ) : convs.map((c) => (
          <button key={c.user_id} data-testid={`conv-${c.user_id}`} onClick={() => { setActive(c.user_id); navigate(`/messages/${c.user_id}`); }}
            className={`w-full flex items-center gap-2 p-3 text-left hover:bg-secondary ${active === c.user_id ? "bg-secondary" : ""}`}>
            <img src={fileUrl(c.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${c.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate">{c.last_message}</p></div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="p-3 border-b border-border font-semibold flex items-center gap-2">
              {thread.partner && <img src={fileUrl(thread.partner.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${active}`} alt="" className="w-8 h-8 rounded-full bg-secondary" />}
              {thread.partner?.name || "Chat"}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
              {thread.messages.map((m) => {
                const mine = m.from_user_id === user?.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : ""}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-copper text-white" : "bg-secondary"}`}>{m.text}</div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-border flex items-center gap-2">
              <Input data-testid="dm-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="rounded-full" />
              <Button data-testid="dm-send" onClick={send} size="icon" className="bg-copper hover:bg-copper/90 rounded-full shrink-0"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
